import type { BaseCrmIntegrationObject } from "app/pages/sales-intelligence/domain/crm-integration/types/object-type";
import {
    crmConditionInclusion,
    crmConditionVariantKey,
    toCrmFilterKey,
} from "app/pages/sales-intelligence/domain/crm-integration/utils/crm-filter-key";
import dynamicFiltersAdapter from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/dynamic-filters-adapter";
import type { DynamicFiltersContext } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/dynamic-filters-adapter/types";
import type { SearchFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/searchFilterInstance";
import type {
    CrmCustomFilterGroupEntry,
    ICrmCustomFiltersListViewModel,
} from "app/pages/sales-intelligence/modules/crm-integration/types/filters-view-model";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import createCrmFieldsFilter from "../models/CrmFieldsFilter/factory";
import createCrmScopeFilter from "../models/CrmScopeFilter/factory";
import type { CrmIntegrationObject } from "../types/description.dto";
import { type LeadsCapability, leadsCapabilityMap } from "../types/leadsCapability";
import { leadsFilterCategoryMap } from "../types/leadsFilterCategory";
import type { LeadsSearchFilterInstance, OrderedFilterInstance } from "../types/leadsSearchFilterInstance";
import { providerToApiCategory } from "../types/leadsSearchFilters.dto";
import { withOrder } from "../utils/withOrder";

import { orderOf, orderOfGroupMember } from "./filterOrder";

type FactoryDeps = {
    readonly grants: ReadonlySet<LeadsCapability>;
    readonly filtersListViewModel: ICrmCustomFiltersListViewModel;
    readonly userIntegrationsLists: readonly CrmIntegrationObject[];
};

const buildEntryInstances = (
    entry: CrmCustomFilterGroupEntry,
    integration: BaseCrmIntegrationObject,
): readonly SearchFilterInstance[] => {
    const apiCategory = providerToApiCategory[integration.integrationName];

    if (!apiCategory) {
        return [];
    }

    const ctx: DynamicFiltersContext = {
        category: leadsFilterCategoryMap.crm,
        apiCategory,
        title: entry.name,
        trackingName: entry.name,
        toKey: (fieldKey) => toCrmFilterKey(integration.integrationName, fieldKey),
        widget: entry.widget,
        inclusions: entry.inclusions,
    };

    const supportsExclude = entry.inclusions?.includes(inclusionOptionMap.excludeOnly);

    return dynamicFiltersAdapter.createGroup(
        entry.id,
        entry.fields.map((field) => field.descriptor),
        ctx,
        supportsExclude ? { inclusionOf: crmConditionInclusion, variantKeyOf: crmConditionVariantKey } : undefined,
    );
};

const buildCrmCustomFilterConfig = (
    customFiltersListViewModel: ICrmCustomFiltersListViewModel,
    grants: ReadonlySet<LeadsCapability>,
): readonly OrderedFilterInstance[] => {
    const { integrationObject, entries } = customFiltersListViewModel;

    if (!integrationObject) {
        return [];
    }

    // CRM scope is member 0 of the crm group anchor; the dynamic custom filters fan out after it.
    const crmScopeInstances = grants.has(leadsCapabilityMap.crmScope)
        ? [withOrder(createCrmScopeFilter(), orderOf("crm"))]
        : [];

    return crmScopeInstances.concat(
        entries
            .flatMap((entry) => buildEntryInstances(entry, integrationObject))
            .map((instance, index) =>
                withOrder(
                    instance as LeadsSearchFilterInstance,
                    orderOfGroupMember("crm", crmScopeInstances.length + index),
                ),
            ),
    );
};

export const createCrmFiltersConfig = ({
    grants,
    filtersListViewModel,
    userIntegrationsLists,
}: FactoryDeps): readonly OrderedFilterInstance[] => {
    if (grants.has(leadsCapabilityMap.crmCustomFilters)) {
        return buildCrmCustomFilterConfig(filtersListViewModel, grants);
    }

    if (grants.has(leadsCapabilityMap.crmLegacyFilter)) {
        return [
            withOrder(
                // TODO clean up the siNavigator.getCrmListPageIntegrationName and find a more reliable source (API responce)
                createCrmFieldsFilter(userIntegrationsLists, siNavigator.getCrmListPageIntegrationName),
                orderOf("crm"),
            ),
        ];
    }

    return [];
};
